/**
 * Lineage Object Page - Detailed lineage view for a specific data object
 * 
 * Shows complete lineage visualization including:
 * - Object details and metadata
 * - Upstream dependencies (what this object depends on)
 * - Downstream dependencies (what depends on this object)
 * - Stakeholder information and business impact
 * - Relationship types and details
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CubeIcon,
  ShareIcon,
  UserGroupIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowRightIcon,
  ArrowLeftOnRectangleIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import lineageApiService from '../../api/lineageApiService';
import {
  useStakeholderGroups,
  useAssignStakeholderToObject,
  useUnassignStakeholderFromObject
} from '../../hooks/useStakeholderGroups';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { useUI } from '../../contexts/UIContext';
import { metadataIntegrationService } from '../../services/metadataIntegrationService';
import { FeatureGate } from '../../components/billing';

const LineageObjectPageContent = () => {
  const { tableName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get objectId from navigation state (passed from LineagePage) or null for direct URL access
  const passedObjectId = location.state?.objectId || null;

  const [objectData, setObjectData] = useState(null);
  const [upstreamDependencies, setUpstreamDependencies] = useState([]);
  const [downstreamDependencies, setDownstreamDependencies] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableMetadata, setTableMetadata] = useState(null); // Phase 3.9: Tier status

  // Stakeholder management state
  const [showManageModal, setShowManageModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removingStakeholder, setRemovingStakeholder] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [relationshipType, setRelationshipType] = useState('consumer');
  const [businessImpactLevel, setBusinessImpactLevel] = useState('medium');
  const [impactDescription, setImpactDescription] = useState('');

  // Hooks for stakeholder management
  const { data: allGroups, isLoading: groupsLoading } = useStakeholderGroups();
  const assignStakeholder = useAssignStakeholderToObject();
  const unassignStakeholder = useUnassignStakeholderFromObject();
  const { updateBreadcrumbs } = useUI();

  useEffect(() => {
    loadObjectLineage();
  }, [tableName]);

  // Update breadcrumbs when object data is loaded
  useEffect(() => {
    if (objectData) {
      updateBreadcrumbs([
        { name: 'Lineage', href: '/lineage' },
        { name: objectData.full_name || objectData.object_name || 'Data Object' }
      ]);
    }
  }, [objectData, updateBreadcrumbs]);

  // Phase 3.9: Load table metadata for tier status
  const loadTableMetadata = async (objectData) => {
    if (!objectData || !objectData.connection_id) return;

    try {
      const database = objectData.database_name;
      const schema = objectData.schema_name;
      const tableName = objectData.table_name || objectData.object_name;

      if (!database || !schema || !tableName) return;

      const response = await metadataIntegrationService.getTableMetadataTiered(
        objectData.connection_id,
        { database, schema },
        {}
      );

      if (response && response.success) {
        const table = response.tables?.find(
          t => (t.table_catalog || t.database_name) === database &&
               t.table_schema === schema &&
               t.table_name === tableName
        );

        if (table) {
          setTableMetadata({
            tierStatus: response.tierStatus,
            database: database,
            schema: schema,
            tableName: tableName,
            tier1Complete: table.tier1_collected_at != null,
            tier2Complete: table.tier2_collected_at != null,
            tier3Complete: table.tier3_collected_at != null,
            tier4Complete: table.tier4_collected_at != null,
            tier1CollectedAt: table.tier1_collected_at,
            tier2CollectedAt: table.tier2_collected_at,
            tier3CollectedAt: table.tier3_collected_at,
            tier4CollectedAt: table.tier4_collected_at
          });
        }
      }
    } catch (error) {
      console.error('Error loading table metadata:', error);
    }
  };

  // Phase 3.9: Get tier status badge
  const getTierStatusBadge = () => {
    if (!tableMetadata) return null;

    const completedTiers = [
      tableMetadata.tier1Complete,
      tableMetadata.tier2Complete,
      tableMetadata.tier3Complete,
      tableMetadata.tier4Complete
    ].filter(Boolean).length;

    if (completedTiers === 0) return null;

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Tier {completedTiers}/4 Complete
      </span>
    );
  };

  // Phase 3.9: Get metadata freshness indicator
  const getMetadataFreshnessIndicator = () => {
    if (!tableMetadata) return null;

    // Find the most recent tier collection
    const timestamps = [
      tableMetadata.tier4CollectedAt,
      tableMetadata.tier3CollectedAt,
      tableMetadata.tier2CollectedAt,
      tableMetadata.tier1CollectedAt
    ].filter(Boolean);

    if (timestamps.length === 0) return null;

    // Get most recent timestamp
    const mostRecent = new Date(Math.max(...timestamps.map(t => new Date(t))));
    const now = new Date();
    const hoursSince = (now - mostRecent) / (1000 * 60 * 60);

    let colorClass = 'bg-green-100 text-green-800';
    let label = 'Fresh';

    if (hoursSince > 168) { // > 1 week
      colorClass = 'bg-red-100 text-red-800';
      label = 'Stale';
    } else if (hoursSince > 48) { // > 2 days
      colorClass = 'bg-yellow-100 text-yellow-800';
      label = 'Aging';
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {label} • {Math.floor(hoursSince)}h ago
      </span>
    );
  };

  const loadObjectLineage = async () => {
    try {
      setLoading(true);
      setError(null);

      let objectId = passedObjectId;

      // If no objectId passed via navigation state, need to resolve full_name → uuid
      if (!objectId) {
        console.log('[LineageObjectPage] No objectId in state, resolving full_name:', decodeURIComponent(tableName));
        const resolveResponse = await lineageApiService.resolveObjectByName(decodeURIComponent(tableName));

        if (resolveResponse.success && resolveResponse.data?.id) {
          objectId = resolveResponse.data.id;
          console.log('[LineageObjectPage] Resolved full_name to objectId:', objectId);
        } else {
          console.error('[LineageObjectPage] Failed to resolve object:', resolveResponse);
          setError('Object not found');
          setLoading(false);
          return;
        }
      }

      // Now call the API with the UUID
      const response = await lineageApiService.getObjectLineage(objectId);

      if (response.success) {
        setObjectData(response.data.object);
        setUpstreamDependencies(response.data.upstream_dependencies || []);
        setDownstreamDependencies(response.data.downstream_dependencies || []);
        setStakeholders(response.data.stakeholders || []);

        // Phase 3.9: Load table metadata for tier status
        await loadTableMetadata(response.data.object);
      } else {
        setError(response.error || 'Failed to load object lineage');
      }
    } catch (err) {
      console.error('Error loading object lineage:', err);
      setError('Failed to load object lineage');
    } finally {
      setLoading(false);
    }
  };

  const getObjectTypeIcon = (objectType) => {
    switch (objectType) {
      case 'table': return <CubeIcon className="h-5 w-5 text-blue-500" />;
      case 'view': return <ShareIcon className="h-5 w-5 text-green-500" />;
      default: return <CubeIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getCriticalityBadge = (criticality) => {
    const config = {
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critical' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Medium' },
      low: { bg: 'bg-green-100', text: 'text-green-800', label: 'Low' },
      unknown: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Unknown' }
    };

    const style = config[criticality] || config.unknown;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  const getRelationshipTypeBadge = (relType) => {
    const config = {
      direct: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Direct' },
      indirect: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Indirect' },
      foreign_key: { bg: 'bg-green-100', text: 'text-green-800', label: 'Foreign Key' },
      sql_dependency: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'SQL Dependency' }
    };

    const style = config[relType] || { bg: 'bg-gray-100', text: 'text-gray-800', label: relType || 'Unknown' };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  const handleViewRelatedObject = (relatedObject) => {
    // URL shows human-readable full_name, but pass UUID via state for backend API calls
    navigate(`/lineage/table/${encodeURIComponent(relatedObject.full_name)}`, {
      state: { objectId: relatedObject.id }
    });
  };

  const handleAssignStakeholder = async () => {
    if (!selectedGroup) {
      alert('Please select a stakeholder group');
      return;
    }

    try {
      await assignStakeholder.mutateAsync({
        groupId: selectedGroup,
        dataObjectId: objectData.id,
        relationshipType,
        businessImpactLevel,
        impactDescription: impactDescription.trim() || undefined
      });

      // Reload lineage to get updated stakeholders
      await loadObjectLineage();

      // Reset form and close modal
      setSelectedGroup('');
      setRelationshipType('consumer');
      setBusinessImpactLevel('medium');
      setImpactDescription('');
      setShowManageModal(false);
    } catch (err) {
      console.error('Error assigning stakeholder:', err);
      alert('Failed to assign stakeholder: ' + (err.message || 'Unknown error'));
    }
  };

  const handleUnassignStakeholderClick = (stakeholder) => {
    setRemovingStakeholder(stakeholder);
    setShowRemoveConfirm(true);
  };

  const handleUnassignStakeholderConfirm = async () => {
    if (!removingStakeholder) return;

    try {
      await unassignStakeholder.mutateAsync({
        groupId: removingStakeholder.stakeholder_group_id,
        dataObjectId: objectData.id
      });

      // Reload lineage to get updated stakeholders
      await loadObjectLineage();
    } catch (err) {
      console.error('Error removing stakeholder:', err);
      alert('Failed to remove stakeholder: ' + (err.message || 'Unknown error'));
    } finally {
      setRemovingStakeholder(null);
    }
  };

  const openManageModal = () => {
    console.log('Opening manage modal - Debug info:', {
      allGroups,
      groupsLoading,
      stakeholders,
      availableGroups: allGroups?.filter(group =>
        !stakeholders.some(s => s.stakeholder_group_id === group.id)
      )
    });
    setShowManageModal(true);
    setSelectedGroup('');
    setRelationshipType('consumer');
    setBusinessImpactLevel('medium');
    setImpactDescription('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading object lineage...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Lineage</h3>
        <p className="text-gray-600 text-center mb-4">{error}</p>
        <button
          onClick={() => navigate('/lineage')}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Lineage
        </button>
      </div>
    );
  }

  if (!objectData) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <InformationCircleIcon className="h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Object Not Found</h3>
        <p className="text-gray-600 text-center mb-4">The requested data object could not be found.</p>
        <button
          onClick={() => navigate('/lineage')}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Lineage
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/lineage')}
            className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Lineage
          </button>
          
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{objectData.full_name}</h1>
            <p className="text-gray-600 mt-1">
              Data lineage and impact analysis
            </p>
          </div>
        </div>
      </div>

      {/* Object Details */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Object Details</h2>
            {/* Phase 3.9: Tier status and freshness */}
            {getTierStatusBadge()}
            {getMetadataFreshnessIndicator()}
          </div>
          {getCriticalityBadge(objectData.business_criticality)}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center gap-3">
            {getObjectTypeIcon(objectData.object_type)}
            <div>
              <p className="text-sm font-medium text-gray-900">Type</p>
              <p className="text-sm text-gray-600 capitalize">{objectData.object_type}</p>
            </div>
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-900">Schema</p>
            <p className="text-sm text-gray-600">{objectData.schema_name}</p>
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-900">Database</p>
            <p className="text-sm text-gray-600">{objectData.database_name}</p>
          </div>
          
          {objectData.row_count && (
            <div>
              <p className="text-sm font-medium text-gray-900">Rows</p>
              <p className="text-sm text-gray-600">{objectData.row_count.toLocaleString()}</p>
            </div>
          )}
          
          {objectData.column_count && (
            <div>
              <p className="text-sm font-medium text-gray-900">Columns</p>
              <p className="text-sm text-gray-600">{objectData.column_count}</p>
            </div>
          )}
          
          {objectData.last_updated && (
            <div>
              <p className="text-sm font-medium text-gray-900">Last Updated</p>
              <p className="text-sm text-gray-600">{new Date(objectData.last_updated).toLocaleDateString()}</p>
            </div>
          )}
        </div>
        
        {objectData.description && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-900">Description</p>
            <p className="text-sm text-gray-600 mt-1">{objectData.description}</p>
          </div>
        )}
      </div>

      {/* Lineage Visualization */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Upstream Dependencies */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <ArrowLeftOnRectangleIcon className="h-5 w-5 text-blue-500" />
              <h2 className="text-lg font-semibold text-gray-900">Upstream Dependencies</h2>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {upstreamDependencies.length}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Objects that this object depends on</p>
          </div>
          
          <div className="p-6">
            {upstreamDependencies.length > 0 ? (
              <div className="space-y-4">
                {upstreamDependencies.map((dep, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      {dep.upstream_object && getObjectTypeIcon(dep.upstream_object.object_type)}
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">
                          {dep.upstream_object?.full_name || 'Unknown Object'}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {dep.upstream_object?.row_count?.toLocaleString() || 0} rows
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {getRelationshipTypeBadge(dep.relationship_type)}
                      {dep.upstream_object && (
                        <button
                          onClick={() => handleViewRelatedObject(dep.upstream_object)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ArrowLeftOnRectangleIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No upstream dependencies</h3>
                <p className="mt-1 text-sm text-gray-500">
                  This object doesn't depend on any other data objects.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Downstream Dependencies */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <ArrowRightIcon className="h-5 w-5 text-green-500" />
              <h2 className="text-lg font-semibold text-gray-900">Downstream Dependencies</h2>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {downstreamDependencies.length}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Objects that depend on this object</p>
          </div>
          
          <div className="p-6">
            {downstreamDependencies.length > 0 ? (
              <div className="space-y-4">
                {downstreamDependencies.map((dep, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      {dep.downstream_object && getObjectTypeIcon(dep.downstream_object.object_type)}
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">
                          {dep.downstream_object?.full_name || 'Unknown Object'}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {dep.downstream_object?.row_count?.toLocaleString() || 0} rows
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {getRelationshipTypeBadge(dep.relationship_type)}
                      {dep.downstream_object && (
                        <button
                          onClick={() => handleViewRelatedObject(dep.downstream_object)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          View
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ArrowRightIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No downstream dependencies</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No other objects depend on this object.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stakeholders */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5 text-purple-500" />
                <h2 className="text-lg font-semibold text-gray-900">Stakeholders</h2>
              </div>
              <p className="text-sm text-gray-600 mt-1">Teams and groups that depend on this object</p>
            </div>
            <button
              onClick={openManageModal}
              className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <PlusIcon className="h-4 w-4" />
              Add Stakeholder
            </button>
          </div>
        </div>

        <div className="p-6">
          {stakeholders.length === 0 ? (
            <div className="text-center py-8">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No stakeholders assigned</h3>
              <p className="mt-1 text-sm text-gray-500">
                Click "Add Stakeholder" to assign a team or group to this data object.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stakeholders.map((stakeholder, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg relative group">
                  <button
                    onClick={() => handleUnassignStakeholderClick(stakeholder)}
                    className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove stakeholder"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                  <div className="flex items-center gap-2 mb-2">
                    <UserGroupIcon className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-medium text-gray-900">
                      {stakeholder.stakeholder_group?.group_name || 'Unknown Group'}
                    </h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {stakeholder.relationship_type && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                        {stakeholder.relationship_type}
                      </span>
                    )}
                    {stakeholder.business_impact_level && (
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        stakeholder.business_impact_level === 'critical'
                          ? 'bg-red-100 text-red-800'
                          : stakeholder.business_impact_level === 'high'
                          ? 'bg-orange-100 text-orange-800'
                          : stakeholder.business_impact_level === 'medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {stakeholder.business_impact_level} impact
                      </span>
                    )}
                  </div>
                  {stakeholder.impact_description && (
                    <p className="text-xs text-gray-600 mt-2">{stakeholder.impact_description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Impact Summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <ChartBarIcon className="h-5 w-5 text-orange-500" />
          <h2 className="text-lg font-semibold text-gray-900">Impact Summary</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">{upstreamDependencies.length}</p>
            <p className="text-sm text-gray-600">Upstream Objects</p>
          </div>

          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">{downstreamDependencies.length}</p>
            <p className="text-sm text-gray-600">Downstream Objects</p>
          </div>

          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600">{stakeholders.length}</p>
            <p className="text-sm text-gray-600">Stakeholder Groups</p>
          </div>
        </div>
      </div>

      {/* Add Stakeholder Modal */}
      {showManageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add Stakeholder</h3>
              <button
                onClick={() => setShowManageModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Stakeholder Group Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stakeholder Group
                </label>
                {groupsLoading ? (
                  <div className="flex justify-center py-2">
                    <LoadingSpinner size="sm" />
                  </div>
                ) : !allGroups || allGroups.length === 0 ? (
                  <div className="text-sm text-gray-600 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    No stakeholder groups available. Create one first from Admin Settings.
                  </div>
                ) : (
                  <>
                    <select
                      value={selectedGroup}
                      onChange={(e) => setSelectedGroup(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select a group...</option>
                      {allGroups?.filter(group =>
                        !stakeholders.some(s => s.stakeholder_group_id === group.id)
                      ).map(group => (
                        <option key={group.id} value={group.id}>
                          {group.group_name}
                        </option>
                      ))}
                    </select>
                    {allGroups.filter(group =>
                      !stakeholders.some(s => s.stakeholder_group_id === group.id)
                    ).length === 0 && (
                      <p className="text-sm text-gray-500 mt-2">
                        All available groups are already assigned to this object.
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Available groups: {allGroups.length} total, {allGroups.filter(group =>
                        !stakeholders.some(s => s.stakeholder_group_id === group.id)
                      ).length} unassigned
                    </p>
                  </>
                )}
              </div>

              {/* Relationship Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relationship Type
                </label>
                <select
                  value={relationshipType}
                  onChange={(e) => setRelationshipType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="consumer">Consumer</option>
                  <option value="owner">Owner</option>
                  <option value="dependent">Dependent</option>
                  <option value="reviewer">Reviewer</option>
                </select>
              </div>

              {/* Business Impact Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Impact Level
                </label>
                <select
                  value={businessImpactLevel}
                  onChange={(e) => setBusinessImpactLevel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              {/* Impact Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Impact Description (Optional)
                </label>
                <textarea
                  value={impactDescription}
                  onChange={(e) => setImpactDescription(e.target.value)}
                  rows={3}
                  placeholder="Describe how this group depends on or uses this data object..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowManageModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignStakeholder}
                disabled={!selectedGroup || assignStakeholder.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {assignStakeholder.isPending ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Adding...
                  </>
                ) : (
                  'Add Stakeholder'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Stakeholder Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemoveConfirm}
        onClose={() => {
          setShowRemoveConfirm(false);
          setRemovingStakeholder(null);
        }}
        onConfirm={handleUnassignStakeholderConfirm}
        title="Remove Stakeholder"
        message={`Are you sure you want to remove ${removingStakeholder?.stakeholder_group?.group_name} from this data object?`}
        confirmLabel="Remove"
        variant="danger"
      />
    </div>
  );
};

// Wrapper component with feature gate
const LineageObjectPage = () => {
  return (
    <FeatureGate
      feature="lineage_enabled"
      upgradeMessage="View detailed lineage information for data objects."
    >
      <LineageObjectPageContent />
    </FeatureGate>
  );
};

export default LineageObjectPage;